import { X } from "lucide-react"
import { useCallback, useEffect, useRef } from "react"

interface StoolSheetProps {
  open: boolean
  onClose: () => void
  onLog: (entry: { type: number; title: string }) => void
}

interface BristolType {
  type: number
  title: string
  subtitle: string
  tintVar: string
  icon: () => React.ReactNode
}

function StoolIcon1() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="6" cy="8" r="3" fill="var(--fig-entry-stool-icon)" />
      <circle cx="14" cy="6" r="2.5" fill="var(--fig-entry-stool-icon)" />
      <circle cx="18" cy="12" r="2" fill="var(--fig-entry-stool-icon)" />
      <circle cx="10" cy="14" r="2.5" fill="var(--fig-entry-stool-icon)" />
      <circle cx="16" cy="18" r="3" fill="var(--fig-entry-stool-icon)" />
      <circle cx="6" cy="17" r="2" fill="var(--fig-entry-stool-icon)" />
    </svg>
  )
}

function StoolIcon2() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <rect
        x="3"
        y="8"
        width="18"
        height="8"
        rx="4"
        fill="var(--fig-entry-stool-icon)"
      />
      <circle cx="7" cy="9" r="1.5" fill="var(--fig-glyph-bump)" />
      <circle cx="12" cy="9.5" r="1.5" fill="var(--fig-glyph-bump)" />
      <circle cx="17" cy="9" r="1.5" fill="var(--fig-glyph-bump)" />
    </svg>
  )
}

function StoolIcon3() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <rect
        x="3"
        y="8"
        width="18"
        height="8"
        rx="4"
        fill="var(--fig-entry-stool-icon)"
      />
      <line
        x1="8"
        y1="8"
        x2="9"
        y2="16"
        stroke="var(--fig-glyph-circle-bg)"
        strokeWidth="1.5"
      />
      <line
        x1="13"
        y1="8"
        x2="14"
        y2="16"
        stroke="var(--fig-glyph-circle-bg)"
        strokeWidth="1.5"
      />
      <line
        x1="17"
        y1="8"
        x2="18"
        y2="16"
        stroke="var(--fig-glyph-circle-bg)"
        strokeWidth="1.5"
      />
    </svg>
  )
}

function StoolIcon4() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <rect
        x="3"
        y="8"
        width="18"
        height="8"
        rx="4"
        fill="var(--fig-entry-stool-icon)"
      />
    </svg>
  )
}

function StoolIcon5() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <ellipse
        cx="7"
        cy="12"
        rx="5"
        ry="4"
        fill="var(--fig-entry-stool-icon)"
      />
      <ellipse
        cx="17"
        cy="12"
        rx="4.5"
        ry="3.5"
        fill="var(--fig-entry-stool-icon)"
      />
    </svg>
  )
}

function StoolIcon6() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <ellipse
        cx="7"
        cy="10"
        rx="4.5"
        ry="3"
        fill="var(--fig-entry-stool-icon)"
        opacity="0.7"
      />
      <ellipse
        cx="15"
        cy="13"
        rx="5"
        ry="3.5"
        fill="var(--fig-entry-stool-icon)"
        opacity="0.7"
      />
      <ellipse
        cx="19"
        cy="8"
        rx="2"
        ry="2"
        fill="var(--fig-entry-stool-icon)"
        opacity="0.5"
      />
    </svg>
  )
}

function StoolIcon7() {
  return (
    <svg width={22} height={22} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M3 12 Q7 7 12 12 Q17 17 21 12"
        stroke="var(--fig-entry-stool-icon)"
        strokeWidth="2.5"
        fill="none"
        strokeLinecap="round"
      />
      <path
        d="M5 16 Q9 13 13 16 Q17 19 21 16"
        stroke="var(--fig-entry-stool-icon)"
        strokeWidth="1.5"
        fill="none"
        strokeLinecap="round"
        opacity="0.4"
      />
    </svg>
  )
}

const bristolTypes: BristolType[] = [
  {
    type: 1,
    title: "Separate hard lumps",
    subtitle: "Like nuts, hard to pass",
    tintVar: "var(--fig-tint-1)",
    icon: () => <StoolIcon1 />,
  },
  {
    type: 2,
    title: "Lumpy sausage",
    subtitle: "Sausage-shaped but lumpy",
    tintVar: "var(--fig-tint-2)",
    icon: () => <StoolIcon2 />,
  },
  {
    type: 3,
    title: "Cracked sausage",
    subtitle: "Cracks on the surface",
    tintVar: "var(--fig-tint-3)",
    icon: () => <StoolIcon3 />,
  },
  {
    type: 4,
    title: "Smooth, soft",
    subtitle: "Smooth and soft like a snake",
    tintVar: "var(--fig-tint-4)",
    icon: () => <StoolIcon4 />,
  },
  {
    type: 5,
    title: "Soft blobs",
    subtitle: "Clear-cut edges, easy to pass",
    tintVar: "var(--fig-tint-5)",
    icon: () => <StoolIcon5 />,
  },
  {
    type: 6,
    title: "Mushy, fluffy",
    subtitle: "Fluffy pieces, ragged edges",
    tintVar: "var(--fig-tint-6)",
    icon: () => <StoolIcon6 />,
  },
  {
    type: 7,
    title: "Entirely liquid",
    subtitle: "Watery, no solid pieces",
    tintVar: "var(--fig-tint-7)",
    icon: () => <StoolIcon7 />,
  },
]

export function StoolSheet({ open, onClose, onLog }: StoolSheetProps) {
  const overlayRef = useRef<HTMLDivElement>(null)
  const closeRef = useRef<HTMLButtonElement>(null)

  const handleOverlayClick = useCallback(
    (e: React.MouseEvent) => {
      if (e.target === overlayRef.current) {
        onClose()
      }
    },
    [onClose],
  )

  // Escape key closes the sheet
  useEffect(() => {
    if (!open) return
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose()
    }
    document.addEventListener("keydown", handler)
    return () => document.removeEventListener("keydown", handler)
  }, [open, onClose])

  // Focus the close button when sheet opens
  useEffect(() => {
    if (open) {
      closeRef.current?.focus()
    }
  }, [open])

  const handleRowActivate = useCallback(
    (entry: BristolType, row: HTMLDivElement) => {
      row.classList.add("stool-flash-logged")
      setTimeout(() => {
        onLog({ type: entry.type, title: entry.title })
        row.classList.remove("stool-flash-logged")
      }, 300)
    },
    [onLog],
  )

  const handleRowKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>, entry: BristolType) => {
      if (e.key === "Enter" || e.key === " ") {
        e.preventDefault()
        handleRowActivate(entry, e.currentTarget)
      }
    },
    [handleRowActivate],
  )

  return (
    <div
      ref={overlayRef}
      className={`stool-sheet-overlay fixed inset-0 z-30 ${open ? "open" : ""}`}
      style={{ background: "#00000040", overscrollBehavior: "contain" }}
      onClick={handleOverlayClick}
      role="dialog"
      aria-modal="true"
      aria-labelledby="stool-sheet-title"
    >
      <div
        className="stool-sheet-panel fixed right-0 bottom-0 left-0 flex flex-col"
        style={{
          background: "var(--fig-action-sheet-bg)",
          borderRadius: "20px 20px 0 0",
          boxShadow: "var(--fig-action-sheet-shadow)",
          maxHeight: "85dvh",
        }}
      >
        {/* Handle + Header */}
        <div className="shrink-0 px-5 pt-3 pb-2">
          <div className="mb-3 flex justify-center">
            <div
              style={{
                width: 36,
                height: 4,
                borderRadius: 2,
                background: "var(--fig-divider-color)",
              }}
            />
          </div>
          <div className="flex items-center justify-between">
            <h2
              id="stool-sheet-title"
              style={{ fontSize: 18, fontWeight: 700 }}
            >
              Log Stool
            </h2>
            <button
              ref={closeRef}
              type="button"
              onClick={onClose}
              aria-label="Close"
              style={{
                background: "none",
                border: "none",
                cursor: "pointer",
                padding: 4,
                color: "var(--fig-text-inactive)",
              }}
            >
              <X size={20} aria-hidden="true" />
            </button>
          </div>
          <p
            style={{
              fontSize: 13,
              color: "var(--fig-text-inactive)",
              marginTop: 4,
            }}
          >
            Tap a type to log instantly
          </p>
        </div>

        {/* Bristol Stool Chart */}
        <div className="flex-1 overflow-y-auto px-5 pb-6">
          <div className="flex flex-col" style={{ gap: 2, paddingTop: 4 }}>
            {bristolTypes.map((entry) => (
              <div
                key={entry.type}
                className="stool-type-row flex items-center"
                role="button"
                tabIndex={0}
                style={{ gap: 14, paddingTop: 10, paddingBottom: 10 }}
                onClick={(e) => handleRowActivate(entry, e.currentTarget)}
                onKeyDown={(e) => handleRowKeyDown(e, entry)}
              >
                <div
                  className="flex shrink-0 items-center justify-center"
                  aria-hidden="true"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 20,
                    background: entry.tintVar,
                  }}
                >
                  {entry.icon()}
                </div>
                <div
                  className="flex min-w-0 flex-1 flex-col"
                  style={{ gap: 1 }}
                >
                  <div className="flex items-center" style={{ gap: 8 }}>
                    <span
                      style={{
                        fontSize: 14,
                        fontWeight: 600,
                        color: "var(--fig-text-primary)",
                      }}
                    >
                      Type {entry.type} &mdash; {entry.title}
                    </span>
                    {entry.type === 4 && (
                      <span
                        style={{
                          fontSize: 10,
                          fontWeight: 600,
                          color: "var(--fig-glyph-ideal-tag-text)",
                          background: "var(--fig-glyph-ideal-bg)",
                          padding: "2px 6px",
                          borderRadius: 4,
                          textTransform: "uppercase",
                          letterSpacing: "0.05em",
                          lineHeight: "12px",
                        }}
                      >
                        Ideal
                      </span>
                    )}
                  </div>
                  <span
                    style={{
                      fontSize: 13,
                      color: "var(--fig-text-avatar)",
                    }}
                  >
                    {entry.subtitle}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
